export type TutorialDifficulty = "debutant" | "avance";

export interface TutorialStep {
  /** Position affichee pour cette etape (notation FEN). */
  fen: string;
  /** Ce que raconte le Taverneu, dans la bulle persistante. */
  narration: string;
  /** Case (notation algebrique) dont on montre les coups legaux automatiquement. */
  selectSquare?: string;
  /** Cases mises en evidence manuellement (etapes sans piece selectionnee). */
  targetSquares?: string[];
  /**
   * Filtre choisi au lancement du tutoriel de strategie (cf. `showDifficultyModal`) ;
   * absent = etape "socle" (intro/conclusion) affichee quel que soit le niveau choisi.
   * Sans effet sur `TUTORIAL_STEPS` (le tutoriel des regles de base n'est pas filtre).
   */
  difficulty?: TutorialDifficulty;
}

/**
 * Sequence scenarisee et fixe (pas de branchement) : chaque etape est une
 * position FEN autonome, pas une partie qui avance reellement — la selection
 * (et donc les cases en surbrillance) est recalculee via le vrai moteur de
 * regles (`legalMovesFrom`), pas une liste ecrite a la main, pour rester
 * garantie exacte.
 */
export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  {
    fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    narration:
      "Bienvenue a l'echiquier, l'ami ! Laisse-moi t'apprendre les bases, pas a pas. Le but du jeu : mettre le roi adverse echec et mat, sans aucune case ou il puisse fuir.",
  },
  {
    fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    narration:
      "Le plateau compte 64 cases, huit rangees et huit colonnes, en damier clair/fonce. Chaque camp commence avec 16 pieces : 8 pions, 2 tours, 2 cavaliers, 2 fous, une dame et un roi.",
    targetSquares: ["d4", "e4", "d5", "e5"],
  },
  {
    fen: "4k3/8/8/8/8/8/4P3/4K3 w - - 0 1",
    narration:
      "Le pion avance tout droit d'une seule case — ou de deux, mais seulement depuis sa case de depart. Il ne recule jamais.",
    selectSquare: "e2",
  },
  {
    fen: "4k3/8/8/3p4/4P3/8/8/4K3 w - - 0 1",
    narration:
      "Pour capturer, le pion se deplace en diagonale d'une case, jamais tout droit. C'est la seule piece qui se deplace et capture differemment.",
    selectSquare: "e4",
  },
  {
    fen: "4k3/8/8/8/3N4/8/8/4K3 w - - 0 1",
    narration:
      "Le cavalier saute en 'L' : deux cases dans une direction, puis une sur le cote. C'est la seule piece qui peut sauter par-dessus les autres.",
    selectSquare: "d4",
  },
  {
    fen: "4k3/8/8/8/3B4/8/8/4K3 w - - 0 1",
    narration:
      "Le fou se deplace en diagonale, aussi loin qu'il veut, tant que rien ne le bloque. Il reste sur la meme couleur de case toute la partie.",
    selectSquare: "d4",
  },
  {
    fen: "4k3/8/8/8/3R4/8/8/4K3 w - - 0 1",
    narration: "La tour se deplace en ligne droite, verticalement ou horizontalement, aussi loin qu'elle veut.",
    selectSquare: "d4",
  },
  {
    fen: "4k3/8/8/8/3Q4/8/8/4K3 w - - 0 1",
    narration: "La dame est la piece la plus puissante de l'echiquier : elle combine les deplacements de la tour et du fou.",
    selectSquare: "d4",
  },
  {
    fen: "4k3/8/8/8/8/8/8/4K3 w - - 0 1",
    narration:
      "Le roi ne se deplace que d'une seule case, dans n'importe quelle direction — mais jamais sur une case attaquee. Quand il est menace, on dit qu'il est en echec : il faut immediatement parer la menace.",
    selectSquare: "e1",
  },
  {
    fen: "4k3/8/8/8/8/8/8/R3K2R w KQ - 0 1",
    narration:
      "Le roque : le roi et une tour bougent ensemble en un seul coup, si aucun des deux n'a encore joue et que rien ne les separe. Petit roque du cote roi, grand roque du cote dame.",
    selectSquare: "e1",
  },
  {
    fen: "4k3/8/8/3pP3/8/8/8/4K3 w - d6 0 1",
    narration:
      "La prise en passant : si un pion adverse vient d'avancer de deux cases juste a cote du tien, tu peux le capturer comme s'il n'avait avance que d'une case — mais seulement au coup d'apres, jamais plus tard.",
    selectSquare: "e5",
  },
  {
    fen: "4k3/P7/8/8/8/8/8/4K3 w - - 0 1",
    narration:
      "Un pion qui atteint la derniere rangee se transforme aussitot en dame, tour, fou ou cavalier, au choix du joueur. Souvent decisif en fin de partie.",
    selectSquare: "a7",
  },
  {
    fen: "rnb1kbnr/pppp1ppp/8/4p3/6Pq/5P2/PPPPP2P/RNBQKBNR w KQkq - 0 3",
    narration:
      "Et voila l'echec et mat : le roi est attaque, sans case pour fuir ni piece pour bloquer ou capturer l'attaquant. La partie s'arrete net. A toi de jouer, maintenant, l'ami !",
  },
];
